/**
 * BackNavigation Component
 * 
 * Global back navigation component that provides consistent back navigation
 * across the Zodiac Workforce Intelligence Institute website.
 * 
 * Features:
 * - Context-aware labels (e.g., "Back to Zodiac Lab")
 * - Browser history back when available
 * - Fallback to logical parent route
 * - Accessible with keyboard and screen readers
 * - Responsive design for mobile and desktop
 */

import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Parent route mapping - defines logical navigation hierarchy
const PARENT_ROUTES = {
  // Top-level pages → Home
  "/wait-theory": { path: "/", label: "Home" },
  "/programs": { path: "/", label: "Home" },
  "/admissions": { path: "/", label: "Home" },
  "/research": { path: "/", label: "Home" },
  "/zodiac-lab": { path: "/", label: "Home" },
  "/observatories": { path: "/", label: "Home" },
  "/juswork": { path: "/", label: "Home" },
  "/about": { path: "/", label: "Home" },
  "/contact": { path: "/", label: "Home" },
  
  // Research subpages → Research
  "/research/overview": { path: "/research", label: "Research" },
  "/research/reports": { path: "/research", label: "Research" },
  "/research/governance": { path: "/research", label: "Research" },
  
  // Zodiac Lab subpages → Zodiac Lab
  "/zodiac-lab/workforce": { path: "/zodiac-lab", label: "Zodiac Lab" },
  "/zodiac-lab/organization": { path: "/zodiac-lab", label: "Zodiac Lab" },
  "/zodiac-lab/institutional": { path: "/zodiac-lab", label: "Zodiac Lab" },
  "/zodiac-lab/workforce/opportunities": { path: "/zodiac-lab/workforce", label: "Workforce Lab" },
  "/zodiac-lab/workforce/tracks": { path: "/zodiac-lab/workforce", label: "Workforce Lab" },
  
  // Observatories subpages → Observatories
  "/observatories/ai": { path: "/observatories", label: "Observatories" },
  "/observatories/global": { path: "/observatories", label: "Observatories" },
  "/observatories/ai/registry": { path: "/observatories/ai", label: "AI Observatory" },
  "/observatories/global/survey": { path: "/observatories/global", label: "Global Observatory" },
  "/observatories/global/results": { path: "/observatories/global", label: "Global Observatory" },
  "/observatories/global/dashboard": { path: "/observatories/global", label: "Global Observatory" },
  "/observatories/global/reports": { path: "/observatories/global", label: "Global Observatory" },
  "/observatories/workforce-dashboard": { path: "/observatories", label: "Observatories" },
  
  // JusWork subpages → JusWork
  "/juswork/profile": { path: "/juswork", label: "JusWork" },
  "/juswork/dashboard": { path: "/juswork", label: "JusWork" },
  "/juswork/assessments": { path: "/juswork", label: "JusWork" },
  "/juswork/observatory/results": { path: "/juswork", label: "JusWork" },
  
  // Programs subpages → Programs
  "/programs/executive-masters": { path: "/programs", label: "Programs" },
  "/programs/training-certificates": { path: "/programs", label: "Programs" },
  "/programs/courses": { path: "/programs", label: "Programs" },
  "/programs/licensing": { path: "/programs", label: "Programs" },
  
  // Auth pages → Home
  "/auth/login": { path: "/", label: "Home" },
  "/auth/register": { path: "/", label: "Home" },
  
  // Admin pages → JusWork or Home
  "/admin/analytics": { path: "/juswork", label: "JusWork" },
};

/**
 * Find the parent route for a given path
 * Handles exact matches and pattern-based fallbacks
 */
function getParentRoute(pathname) {
  // Exact match
  if (PARENT_ROUTES[pathname]) {
    return PARENT_ROUTES[pathname];
  }
  
  // Pattern-based matching for dynamic routes
  // e.g., /zodiac-lab/workforce/tracks/123 → /zodiac-lab/workforce
  const segments = pathname.split('/').filter(Boolean);
  
  // Try progressively shorter paths
  while (segments.length > 1) {
    segments.pop();
    const parentPath = '/' + segments.join('/');
    if (PARENT_ROUTES[parentPath]) {
      return PARENT_ROUTES[parentPath];
    }
    // Check if the parent path exists as a key
    const matchingKey = Object.keys(PARENT_ROUTES).find(key => key === parentPath);
    if (matchingKey) {
      return PARENT_ROUTES[matchingKey];
    }
  }
  
  // Fallback based on first segment
  if (segments.length >= 1) {
    const topLevel = '/' + segments[0];
    if (PARENT_ROUTES[topLevel]) {
      return PARENT_ROUTES[topLevel];
    }
    // Default: go to that top-level route
    return { path: topLevel, label: formatLabel(segments[0]) };
  }
  
  // Ultimate fallback
  return { path: "/", label: "Home" };
}

/**
 * Format a path segment into a readable label
 */
function formatLabel(segment) {
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * BackNavigation Component
 * 
 * @param {Object} props
 * @param {string} props.fallbackPath - Override the automatic parent detection
 * @param {string} props.fallbackLabel - Override the automatic label
 * @param {string} props.className - Additional CSS classes
 */
export default function BackNavigation({ fallbackPath, fallbackLabel, className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Don't show on homepage
  if (location.pathname === "/" || location.pathname === "") {
    return null;
  }
  
  // Get parent route info
  const parentInfo = fallbackPath 
    ? { path: fallbackPath, label: fallbackLabel || formatLabel(fallbackPath.split('/').pop()) }
    : getParentRoute(location.pathname);
  
  const handleBack = () => {
    // Check if we have history to go back to
    if (window.history.length > 2) {
      // Use browser back for better UX
      navigate(-1);
    } else {
      // Fallback to parent route
      navigate(parentInfo.path);
    }
  };
  
  return (
    <button
      onClick={handleBack}
      className={`
        inline-flex items-center gap-2 
        text-gray-700 hover:text-gray-900
        transition-colors duration-200
        py-2 px-1 -ml-1
        rounded-md
        focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2
        group
        ${className}
      `}
      aria-label={`Go back to ${parentInfo.label}`}
      data-testid="back-navigation"
    >
      <ArrowLeft 
        className="w-5 h-5 transition-transform group-hover:-translate-x-0.5" 
        strokeWidth={2}
      />
      <span className="text-sm font-medium">
        Back to {parentInfo.label}
      </span>
    </button>
  );
}

/**
 * Compact version for tight spaces (arrow only with tooltip)
 */
export function BackNavigationCompact({ fallbackPath, fallbackLabel, className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  if (location.pathname === "/" || location.pathname === "") {
    return null;
  }
  
  const parentInfo = fallbackPath 
    ? { path: fallbackPath, label: fallbackLabel || "Back" }
    : getParentRoute(location.pathname);
  
  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(parentInfo.path);
    }
  };
  
  return (
    <button
      onClick={handleBack}
      className={`
        inline-flex items-center justify-center
        w-10 h-10
        text-gray-700 hover:text-gray-900 hover:bg-gray-100
        transition-all duration-200
        rounded-full
        focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2
        ${className}
      `}
      aria-label={`Go back to ${parentInfo.label}`}
      title={`Back to ${parentInfo.label}`}
      data-testid="back-navigation-compact"
    >
      <ArrowLeft className="w-5 h-5" strokeWidth={2} />
    </button>
  );
}
